import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Card } from 'tea-component';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import useMobileStoreRankRegionOptions from '@intelligence/v3/_hooks/useMobileStoreRankRegionOptions';
import { analysisPlatformTopInfo } from '@/pages/intelligence/_config/storeRank/utils';
import { MOBILE_SOURCES } from '@/pages/intelligence/_config/storeRank/config';
import { GENRE_SOURCE_MAP, GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import { getInitialFormValue } from './utils';
import {
  // 单数据源筛选器
  SINGLE_SOURCE_SELECT as SINGLE_SOURCE_SELECT_FILTER,
  // 级联国家筛选器
  REGION as REGION_SELECT_FILTER,
  // 品类筛选器
  GENRETOR as GENRE_SELECT_FILTER,
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 单平台筛选器
  SINGLE_PLATFORM_SELECT as SINGLE_PLATFORM_SELECT_FILTER,
  // Product Model筛选器
  SENSOR_TOWER_TAG_PRODUCT_MODEL,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { DEFAULT_FORM_VALUE, STATIC_FILTER_CONFIGS } from './config';
import '../AllTopChartsFilter/index.scss';
import type { IBaseProps, IRegionType, IRegion, IDateTypely } from '@/pages/intelligence/types/common';
import type { IMobileStoreRankTopGlobalCompareFilter } from '@/pages/intelligence/types/storeRank';
import useFilterUseHook from '@/pages/intelligence/hooks/useFilterUseHook';
import { formatFilterSaveValue } from '@intelligence/utils/filterUtils';

interface IGlobalCompareFilterProps extends IBaseProps {
  // 筛选器右侧的info提示
  iconText?: React.ReactNode;
  isOperate: boolean;
  granularity?: IDateTypely;
  startDate?: string;
  endDate?: string;
  filter?: any;
  onConfirm?: (payload: IMobileStoreRankTopGlobalCompareFilter) => void;
  onReset?: () => void;
  rangeTimeComponentDefault: any;
}

export const convertRegionTypeByMarkets = (regions?: string[]): IRegionType => {
  return regions?.length === 1 && ['global', 'global_wo_china'].includes(regions?.[0]) ? 'region' : 'market';
};

const GlobalCompareFilter: React.FC<IGlobalCompareFilterProps> = (props) => {
  const { style, className, isOperate, iconText, startDate, endDate, filter, onConfirm, onReset, rangeTimeComponentDefault, entityType } = props;

  // const { formatMessage } = useIntl();

  /**
   * 继承逻辑在这写
   * formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
   */
  const { filterExtend, setFilterExtend, resetFilterExtend } = useFilterExtend({});
  const { tagConfig } = useSenTowerTag({});

  const { regionOptions } = useMobileStoreRankRegionOptions();

  // 筛选器的初始值
  const initialFormValue = getInitialFormValue(isOperate, filterExtend, DEFAULT_FORM_VALUE);

  // 筛选器UI渲染需要的value
  // const formValue = useRef<any>(initialFormValue);

  const [formValue, setFormValue] = useState(initialFormValue);

  const formatFormValue = (values: any) => {
    const newFilterValues = formatFilterSaveValue({
      values,
      timeConfig: {
        // 天粒度单选1天
        needSameDay: true,
      },
    });
    return newFilterValues;
  };

  const {
    filterName,
    filterData,
    updateFormValue,
    onSaveFilterMsg,
    onFilterChange,
  } = useFilterUseHook({
    defaultValue: DEFAULT_FORM_VALUE,
    formValue,
    setFormValue,
    timeFilterValues: {
      type: 'daily', // granularity
      value: [startDate, endDate],
    },
    formatFormValue,
  });


  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,

    handleGenresChange,
    handleReset: handleGenresReset,
  } = useGenreComponent(props, undefined, { defaultGenreSource: GENRE_SOURCE_MAP.APP_MAGIC });

  // 动态筛选器配置，即全部或者部分配置 需要通过组件props、组件其他state、组件其他变量来决定，不能单独写在一个js文件中
  const dynamicFilterConfigs = {
    genre: {
      key: GENRE_SELECT_FILTER,
      type: GENRE_SELECT_FILTER,
      visible: 1,
      disabled: false,
      genreData: {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        level3Genres,
        level4Genres,
        handleGenresChange,
        handleGenresReset,
      },
      entityType,
      sources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
      // 启用标准化品类参数处理，使 apiValue 包含 appmagic_genre 等字段
      isStdGenreValuesHandler: true,
    },
  };

  const [inputList, setInputList] = useState([
    STATIC_FILTER_CONFIGS.MY_FILTER,
    STATIC_FILTER_CONFIGS.SINGLE_SOURCE,
    STATIC_FILTER_CONFIGS.REGION,
    STATIC_FILTER_CONFIGS.RANGE_TIME,
    dynamicFilterConfigs.genre,
    STATIC_FILTER_CONFIGS.SENSOR_TOWER_TAG_PRODUCT_MODEL,
  ]);

  const updateRegionFilterOptions = (inputList: any[], regionOptions: IRegion[] | null) => {
    const regionFilterInput = inputList?.find((item) => item?.key === REGION_SELECT_FILTER);
    if (regionFilterInput && Reflect.has(regionFilterInput, 'options')) {
      regionFilterInput.options = regionOptions ?? [];
    }
  };

  const updateProductModelOptions = (inputList: any[], tagConfig: any) => {
    const productModelInput = inputList?.find((item) => item?.key === SENSOR_TOWER_TAG_PRODUCT_MODEL);
    if (productModelInput) {
      productModelInput.options = tagConfig?.[SENSOR_TOWER_TAG_PRODUCT_MODEL] || [];
    }
  };

  const updateTimeComponentDefault = (inputListParam: any[]) => {
    const inputListTmp = inputListParam?.map((item) => {
      // 更新最早有数据时间
      if (rangeTimeComponentDefault) {
        if (item?.key === STATIC_FILTER_CONFIGS.RANGE_TIME) {
          return {
            ...item,
            componentDefault: rangeTimeComponentDefault,
          };
        }
      }
      return item;
    });
    return inputListTmp;
  };
  /**
   * 更新各个筛选器的options数据
   */
  useEffect(() => {
    setInputList((prevInputList) => {
      let inputListTmp = prevInputList.map((item) => {
        return { ...item };
      });
      // 更新国家筛选器选项数据
      updateRegionFilterOptions(inputListTmp, regionOptions);
      // 更新 Product Model 筛选器选项数据
      updateProductModelOptions(inputListTmp, tagConfig);

      inputListTmp = updateTimeComponentDefault(inputListTmp);
      return inputListTmp;
    });
  }, [regionOptions, rangeTimeComponentDefault, tagConfig]);

  /** 时间筛选器值由父组件更新 */
  useEffect(() => {
    updateFormValue();
  }, [filterName, JSON.stringify(filterData), startDate, endDate]);

  /** 监听 filter prop 中的 Product Model 变化，更新筛选器默认值 */
  useEffect(() => {
    const productModelFromFilter = filter?.[SENSOR_TOWER_TAG_PRODUCT_MODEL];
    if (productModelFromFilter?.value?.length > 0) {
      setFormValue((prev: any) => ({
        ...prev,
        [SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
          value: productModelFromFilter.value,
        },
      }));
    }
  }, [filter?.[SENSOR_TOWER_TAG_PRODUCT_MODEL]]);
  // useEffect(() => {
  //   const rangeTimeValue = formValue.current?.[RANGE_TIME_SELECT_FILTER];
  //   formValue.current = {
  //     ...formValue.current,
  //     [RANGE_TIME_SELECT_FILTER]: {
  //       type: granularity ? feachTimeTypeToFilterTimeType(granularity) : rangeTimeValue?.type ?? 'date',
  //       value: startDate && endDate ? [moment(startDate), moment(endDate)] : rangeTimeValue?.value ?? [],
  //     },
  //   };
  // }, [granularity, startDate, endDate]);

  /**
   * 筛选器confirm的回调
   * @param value 筛选器数据（筛选器中各个组件UI渲染需要的value）
   * @param apiValue 处理后的筛选器数据，为了更方便调用接口，针对高频特殊筛选器的数据做了数据格式转换（将筛选器原始的数据转换为更方便接口入参的数据格式，同时也会保留筛选器最原始的数据，用于筛选器继承）
   */
  const handleConfirm = (value: any, apiValue: any) => {
    setFormValue(value);
    const singleSource = apiValue?.[SINGLE_SOURCE_SELECT_FILTER];
    const region = apiValue?.[REGION_SELECT_FILTER];
    const genre = apiValue?.[GENRE_SELECT_FILTER];
    const rangeTime = apiValue?.[RANGE_TIME_SELECT_FILTER];

    const sourceTmp = singleSource?.value ?? MOBILE_SOURCES[0];
    const { platform } = analysisPlatformTopInfo(sourceTmp);
    const regions = region?.markets ?? ['global'];
    const regionType = convertRegionTypeByMarkets(regions);
    const filterTmp = {
      source: sourceTmp,
      region_type: regionType,
      regions,
      date: rangeTime?.start_date ?? rangeTime?.end_date ?? '',
      genre: genre?.genre ?? [],
      sub_genre: genre?.sub_genre ?? [],
      genre_source: genre?.genre_source ?? '',
      iegg_genre_req: genre?.iegg_genre_req ?? [],
      // 传递原始品类数据，供 storeRank.ts 统一处理 filter 对象
      appannie_genre: genre?.appannie_genre || [],
      sensortower_genre: genre?.sensortower_genre || [],
      iegg_genre: genre?.iegg_genre || [],
      appmagic_genre: genre?.appmagic_genre || [],
      [SENSOR_TOWER_TAG_PRODUCT_MODEL]: apiValue[SENSOR_TOWER_TAG_PRODUCT_MODEL],
    };
    const apiValueTmp = {
      ...apiValue,
      // 修改单平台筛选器的继承制（mobile store rank 榜单页 需要继承 Global Compare页面勾选的source所属的platform）
      [SINGLE_PLATFORM_SELECT_FILTER]: {
        value: platform,
      },
    };
    onConfirm?.(filterTmp);

    // 点击确认后，将筛选器保存方案及保存数据存到缓存中
    onSaveFilterMsg(value, apiValue);
    // 榜单国家筛选器在confirm过滤继承值后需要更新继承值，
    // feat国家筛选闪动问题,setFilterExtend放在后面,
    setFilterExtend(apiValueTmp);
  };

  const handleReset = () => {
    const newFormValue = {
      ...DEFAULT_FORM_VALUE,
      [RANGE_TIME_SELECT_FILTER]: {
        type: 'date',
        value: rangeTimeComponentDefault?.date
      },
    };
    setFormValue(newFormValue);
    genreRef.current?.reset();
    resetFilterExtend();
    onReset?.();
  };

  return (
    <Card style={style} className={classnames('dbi-atcfilter', className)}>
      <Card.Body>
        <Filter
          iconText={iconText}
          inputList={inputList}
          defaultValue={{
            ...DEFAULT_FORM_VALUE,
            [RANGE_TIME_SELECT_FILTER]: {
              type: 'date',
              value: rangeTimeComponentDefault?.date
            },
          }}
          value={formValue}
          onConfirm={handleConfirm}
          onReset={handleReset}
          onChange={onFilterChange}
          isHaveFilterSave={true}
        />
      </Card.Body>
    </Card>
  );
};

export default GlobalCompareFilter;
